import * as React from "react";
import { COLORS } from "../lib/brand";
import { Pressable, Text, View } from "react-native";

import { captureSentryException } from "../lib/sentry";

type Props = {
  children: React.ReactNode;
  fallbackMessage?: string;
  /** Goes into the Sentry event as `boundary: "..."`. Use this to
   * disambiguate which portal a crash happened in (root / admin /
   * client / judge). */
  boundaryName?: string;
};

type State = {
  hasError: boolean;
  eventId?: string;
};

/**
 * Per-portal error boundary. The root layout wraps the whole tree;
 * each portal layout wraps its inner Stack so a crash inside one
 * portal renders the fallback in the right pane while the sidebar
 * stays usable. The user can navigate to a healthy page and continue.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    captureSentryException(error, {
      componentStack: info.componentStack,
      boundary: this.props.boundaryName ?? "unknown",
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, eventId: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <View
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#080808",
            padding: 24,
          }}
        >
          <Text
            style={{
              fontFamily: "CormorantGaramond_400Regular",
              color: COLORS.LINEN.hex,
              fontSize: 32,
              marginBottom: 12,
            }}
          >
            Something went wrong.
          </Text>
          <Text
            style={{
              fontFamily: "DMSans_400Regular",
              color: "rgba(247,244,238,0.55)",
              fontSize: 14,
              lineHeight: 21,
              marginBottom: 24,
              maxWidth: 360,
              textAlign: "center",
            }}
          >
            {this.props.fallbackMessage ??
              "We've been notified. Try refreshing — if it keeps happening, email luke@coravu.com."}
          </Text>
          <Pressable
            onPress={this.handleReset}
            style={{
              paddingHorizontal: 20,
              paddingVertical: 10,
              backgroundColor: COLORS.AMBER.hex,
              borderRadius: 6,
            }}
          >
            <Text
              style={{
                fontFamily: "DMSans_700Bold",
                fontSize: 11.5,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: COLORS.FOREST.hex,
              }}
            >
              Try again
            </Text>
          </Pressable>
        </View>
      );
    }
    return this.props.children;
  }
}
